import { locale, localized, t, type MessageKey } from "../i18n";
import { appliedParts, closedRuleText, outcomeName } from "../api/mappers";

/** What a conversation event keeps: who did it (a person, the contact, a flow…), when, and what. */
export interface EventSource {
  kind: string;
  actorKind: string;
  actorId: string | null;
  data: Record<string, unknown>;
  at: string;
}
export interface EventContext {
  memberName: (id: string) => string | undefined;
  teamName: (id: string) => string | undefined;
  contactName: string;
  statusLabel: (status: string) => string;
}
export interface EventDetails {
  who: string;
  when: string;
  rows: Array<{ label: string; value: string }>;
}

const REASONS = [
  "first_reply",
  "self",
  "distribution",
  "manual",
  "integration",
  "business_app_reply",
  "member_reply",
] as const;

function actor(e: EventSource, ctx: EventContext): string {
  switch (e.actorKind) {
    case "member":
      return (e.actorId && ctx.memberName(e.actorId)) || t("message.someone");
    case "contact":
      return ctx.contactName || t("event.actor.contact");
    case "flow":
    case "assistant":
    case "api":
    case "policy":
      return t(`event.actor.${e.actorKind}` as MessageKey);
    default:
      return t("event.actor.system");
  }
}

export function eventWhen(iso: string): string {
  return new Date(iso).toLocaleString(locale.value, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * The "Dados do evento" of a conversation notice: who, when, and what changed (from and to,
 * why, the note). Only what the event recorded; nothing guessed.
 */
export function eventDetails(e: EventSource, ctx: EventContext): EventDetails {
  const d = e.data ?? {};
  const str = (v: unknown) => (typeof v === "string" && v ? v : null);
  const member = (v: unknown) => {
    const id = str(v);
    return id ? (ctx.memberName(id) ?? t("message.someone")) : null;
  };
  const rows: EventDetails["rows"] = [];
  const add = (label: MessageKey, value: string | null) => {
    if (value) rows.push({ label: t(label), value });
  };
  switch (e.kind) {
    case "status_changed":
      add("event.info.from", str(d.from) && ctx.statusLabel(String(d.from)));
      add("event.info.to", str(d.to) && ctx.statusLabel(String(d.to)));
      break;
    case "assigned":
    case "unassigned": {
      add("event.info.from", member(d.from));
      add("event.info.to", member(d.to ?? d.memberId));
      const reason = str(d.reason);
      add(
        "event.info.reason",
        reason
          ? (REASONS as readonly string[]).includes(reason)
            ? t(`event.reason.${reason}` as MessageKey)
            : reason
          : null,
      );
      break;
    }
    case "transferred": {
      const team = (v: unknown) => {
        const id = str(v);
        return id ? (ctx.teamName(id) ?? null) : null;
      };
      const side = (m: unknown, tm: unknown) =>
        [member(m), team(tm)].filter(Boolean).join(" · ") || null;
      add("event.info.from", side(d.fromMember, d.fromTeam));
      add("event.info.to", side(d.toMember, d.toTeam) ?? t("event.info.queue"));
      add("event.info.note", str(d.note));
      break;
    }
    case "snoozed":
      add("event.info.until", str(d.until) && eventWhen(String(d.until)));
      add("event.info.note", str(d.reason));
      break;
    case "closed":
      add("event.info.outcome", d.outcome ? outcomeName(d) : null);
      add("event.info.rule", closedRuleText(d.rule) || null);
      break;
    case "priority_changed":
      add("event.info.from", str(d.from));
      add("event.info.to", str(d.to));
      break;
    case "tool_called":
    case "tool_pending":
    case "tool_decided": {
      // A tool call: which one, what the assistant asked, what came back, and who confirmed.
      add("event.info.tool", str(d.toolName));
      const input =
        d.input && typeof d.input === "object"
          ? Object.entries(d.input as Record<string, unknown>)
              .map(
                ([k, v]) =>
                  `${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`,
              )
              .join(" · ")
          : null;
      add("event.info.tool_input", input || null);
      add("event.info.tool_preview", str(d.preview));
      add("event.info.tool_result", str(d.text));
      if (d.confirmedBy === "contact")
        add(
          "event.info.tool_confirmed",
          ctx.contactName || t("event.actor.contact"),
        );
      add("event.info.tool_approved", member(d.approvedBy));
      if (typeof d.latencyMs === "number" && d.latencyMs > 0)
        add("event.info.tool_time", t("tools.ms", { n: d.latencyMs }));
      break;
    }
    case "button_tapped": {
      // The button, the template and what it applied (decision 0036).
      add("event.info.button", str(d.button));
      add("event.info.template", str(d.template));
      const parts = appliedParts(d.applied);
      add("event.info.applied", parts.length ? parts.join(" · ") : null);
      break;
    }
    case "active_messages_changed":
      add("event.info.note", str(d.note) ?? str(d.reason));
      break;
    case "skill_opened":
      // Which skill the assistant opened, and its version for a library skill (decision 0035).
      add("event.info.skill", str(d.name));
      {
        // Stored as "library v3" or "revision 2".
        const v = /(library v|revision )(\d+)/.exec(String(d.version ?? ""));
        add(
          "event.info.skill_version",
          v
            ? v[1] === "library v"
              ? t("skills.version", { n: Number(v[2]) })
              : t("skills.revision", { n: Number(v[2]) })
            : null,
        );
      }
      break;
    case "handler_changed":
      add(
        "event.info.from",
        str(d.from) && t(`handler.${d.from}` as MessageKey),
      );
      add("event.info.to", str(d.to) && t(`handler.${d.to}` as MessageKey));
      break;
  }
  return { who: actor(e, ctx), when: eventWhen(e.at), rows };
}
